import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { Pagador } from 'src/app/dashboard/interfaces/pagador';
import { PagadorService } from 'src/app/dashboard/services/pagador.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { PagadorComplianceComponent } from '../../pagador-compliance/pagador-compliance.component';

@Component({
  selector: 'app-list-payer',
  templateUrl: './list-payer.component.html',
  styleUrls: ['./list-payer.component.sass']
})
export class ListPayerComponent implements OnInit {
  usuarioID: number | undefined;
  private _paginator?: MatPaginator;
  // Setter: el paginador vive dentro de *ngIf="dataSource", así que se asocia
  // en cuanto aparece (y setData lo reasocia si el dataSource se recrea).
  @ViewChild(MatPaginator) set paginator(mp: MatPaginator | undefined) {
    this._paginator = mp;
    if (mp && this.dataSource) this.dataSource.paginator = mp;
  }
  filtro = '';
  @Output() reloadAccountList: EventEmitter<any> = new EventEmitter();
  @Output() actualizarCuenta: EventEmitter<Pagador> = new EventEmitter();
  @Output() seleccion: EventEmitter<Pagador> = new EventEmitter();
  subscriptions: Subscription = new Subscription();
  @Input() listaCuentas: Pagador[] = [];
  @Input() set setPagadores(pagadores:Pagador[]){
    this.isAdminView= true;
    this.setData(pagadores ?? []);
  }
  isAdminView = false;
  @Input() seleccionable: boolean = false;
  cuentaSeleccionada: Pagador | undefined;

  get displayedColumns(): string[] {
    const cols = [
      'tipoUsuario',
      'nombre',
      'razon_social',
      'tipoId',
      'numeroId',
      'paisResidencia',
      'nacionalidad',
      'estado_compliance',
      'fecha_registro',
      'acciones',
    ];
    // En la vista del operador se muestra el cliente dueño del pagador.
    if (this.isAdminView) cols.splice(1, 0, 'cliente');
    return cols;
  }

  // Las celdas usan `texto()`: un campo vacío se muestra "—" y no "undefined" o
  // "null" (pagadores por link de empresa sin nombre comercial, persona sin
  // apellidos, etc.).
  columns = [
    {
      columnDef: 'tipoUsuario',
      header: 'Tipo',
      cell: (element: any) => (element.tipoUsuarioId === 1 ? 'Empresa' : 'Persona'),
    },
    {
      columnDef: 'paisResidencia',
      header: 'País',
      cell: (element: any) => (element.tipoUsuarioId === 1 ? 'No aplica' : texto(element.paisResidencia)),
    },
    {
      columnDef: 'nacionalidad',
      header: 'Nacionalidad',
      cell: (element: any) => (element.tipoUsuarioId === 1 ? 'No aplica' : texto(element.nacionalidad)),
    },
    {
      columnDef: 'tipoId',
      header: 'Tipo ID',
      cell: (element: any) => (element.tipoUsuarioId === 1 ? 'RUT EMPRESA' : texto(element.tipoId)),
    },
    {
      columnDef: 'numeroId',
      header: 'Documento',
      cell: (element: any) => documentoPagador(element),
    },
    {
      columnDef: 'nombre',
      header: 'Nombre',
      cell: (element: any) => nombrePagador(element),
    },
    {
      columnDef: 'razon_social',
      header: 'Razón social',
      cell: (element: any) => (element.tipoUsuarioId === 1 ? texto(element.razonSocial) : 'No aplica'),
    },
    // 'estado_compliance' se renderiza como badge (ng-container dedicado), no acá.
  ];
  dataSource: MatTableDataSource<any> | undefined;

    constructor(
    private store: Store<AppState>,
    private pagadorService: PagadorService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private spinnerService: SpinnerService
  ) {
    this.initFormCuenta();
  }



  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  initFormCuenta() {
    this.subscriptions.add(
      this.store.select('auth').subscribe(async ({ user }) => {
        this.usuarioID = user?.usuarioId;
        if(this.usuarioID){
          // En la vista del operador la lista entra por `setPagadores`. Esta
          // suscripción corre en el constructor, antes que el @Input, y su
          // respuesta (los pagadores del propio operador, normalmente ninguno)
          // llegaba después y pisaba la lista del cliente revisado.
          if (this.isAdminView) return;
          const propios = await this.pagadorService.getPagadoresByUser(this.usuarioID);
          if (this.isAdminView) return;
          this.listaCuentas = propios;
          this.setData(this.listaCuentas);
        }
      })
    );

  }
  ngOnInit() {}

  /**
   * Crea el dataSource y (re)asocia paginador + filtro. Se llama en cada carga
   * porque MatTableDataSource se reinstancia y perdería la asociación.
   */
  private setData(rows: Pagador[]): void {
    this.dataSource = new MatTableDataSource(rows);
    this.dataSource.filterPredicate = (p: any, filtro: string) => {
      const texto = [
        p.nombre, p.apellidos, p.numeroId, p.rutEmpresa,
        p.razonSocial, p.nombreComercial,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return texto.includes(filtro);
    };
    if (this._paginator) this.dataSource.paginator = this._paginator;
    if (this.filtro) this.dataSource.filter = this.filtro.trim().toLowerCase();
  }

  aplicarFiltro(valor: string): void {
    this.filtro = valor ?? '';
    if (!this.dataSource) return;
    this.dataSource.filter = this.filtro.trim().toLowerCase();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  async reloadAccount(){
    if(this.usuarioID){
      this.listaCuentas = await this.pagadorService.getPagadoresByUser(this.usuarioID);
      this.setData(this.listaCuentas);

    }
  }

  async eliminar(pagadorId: string){
    const resultado = await  Swal.fire({
      text: '¿Está seguro de que desea desvincular esta cuenta?',
      showDenyButton: false,
      showCancelButton: true,
      confirmButtonText: 'Sí',
      cancelButtonText: `No`,
      confirmButtonColor: '#1e5a3f',

    });
    if(resultado.isConfirmed){
      const ref =  this.spinnerService.start();
      try {

      const result = await this.pagadorService.inactivatePagador(pagadorId);
      this.reloadAccount();
      Swal.fire({
        icon: 'success',
        title: 'Pagador eliminado',
        text: 'El pagador ha sido eliminado exitosamente',
        showCancelButton: false,
        showDenyButton: false,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',

      });
      } catch (error: any) {
        // R8: el back explica por qué no (p. ej. un pagador que solo soporte
        // puede dar de baja) sin decir su estado; se muestra tal cual.
        const mensaje = typeof error?.error?.message === 'string'
          ? error.error.message
          : 'Ha ocurrido un error al intentar eliminar el pagador';
        Swal.fire({
          icon: 'error',
          title: 'No se puede eliminar el pagador',
          text: mensaje,
          showCancelButton: false,
          showDenyButton: false,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1e5a3f',

        });
      }
      this.spinnerService.stop(ref);
    }


  }

  actualizar(cuenta: Pagador){
  }
  openPagadorData(pagador:Pagador){
    this.dialog.open(PagadorComplianceComponent,{
      disableClose: true,
      data: {pagador},
      // Responsive: tope de 1000px en desktop pero se achica en pantallas
      // chicas (95vw) y el alto se ajusta al contenido con scroll interno.
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'pagador-compliance-dialog',
    }).afterClosed().subscribe(res=>{
      if(res === true){
        this.reloadAccountList.emit(true);
      }
    })
  }

  /**
   * Badge de estado.
   *  - Operador: estado de compliance (0 por aprobar, 3 aprobado, 1 rechazado,
   *    2 blacklist, 4 bloqueado).
   *  - Cliente: el backend no le manda el estado de compliance (decirle
   *    "BLACKLIST" o "RECHAZADO" es tipping-off), solo `habilitado` y
   *    `bloqueado` (sin el motivo). Antes el badge leía `estadoComplianceID` y
   *    mostraba "Estado undefined".
   */
  /**
   * R8: el cliente no da de baja un pagador bloqueado (ni uno en blacklist,
   * que no ve: ese lo rechaza el back con un mensaje genérico).
   */
  puedeEliminar(row: any): boolean {
    if (this.isAdminView) return false;
    return !(row?.bloqueado ?? Number(row?.estadoComplianceID) === 4);
  }

  estadoBadge(row: any): { label: string; color: string; bg: string } {
    if (!this.isAdminView || row?.estadoComplianceID == null) {
      // `?? …`: con el wallet-back anterior (sin `habilitado`/`bloqueado`) se
      // deduce del estado, así el front se puede desplegar antes que el back.
      const bloqueado = row?.bloqueado ?? Number(row?.estadoComplianceID) === 4;
      if (bloqueado) return { label: 'Bloqueado', color: '#4b5563', bg: '#e5e7eb' };
      const habilitado =
        row?.habilitado ?? (Number(row?.estadoComplianceID) === 3 && row?.activo !== false);
      return habilitado
        ? { label: 'Habilitado', color: '#16a34a', bg: '#f0fdf4' }
        : { label: 'En revisión', color: '#b45309', bg: '#fffbeb' };
    }
    const label = row?.estadoCompliance?.descripcion ?? `Estado ${row?.estadoComplianceID}`;
    switch (row?.estadoComplianceID) {
      case 0: return { label, color: '#ea580c', bg: '#fff7ed' }; // Por aprobar
      case 3: return { label, color: '#16a34a', bg: '#f0fdf4' }; // Aprobado
      case 1: return { label, color: '#dc2626', bg: '#fef2f2' }; // Rechazado
      case 2: return { label, color: '#374151', bg: '#e5e7eb' }; // Blacklist
      case 4: return { label: row?.estadoCompliance?.descripcion ?? 'BLOQUEADO', color: '#6d28d9', bg: '#ede9fe' }; // Bloqueado
      default: return { label, color: '#6b7280', bg: '#f3f4f6' };
    }
  }

  /** Nombre del cliente dueño (persona o empresa). */
  clienteNombre(row: any): string {
    const u = row?.usuario;
    if (!u) return '—';
    const esEmpresa = u.tipoUsuarioID === 1 || !!u.razonSocial;
    if (esEmpresa) return u.razonSocial || u.nombreComercial || '(empresa)';
    return [u.nombre, u.apellidos].filter(Boolean).join(' ') || '—';
  }

  nombre(row: any): string {
    return nombrePagador(row);
  }

  documento(row: any): string {
    return documentoPagador(row);
  }

  texto(valor: unknown): string {
    return texto(valor);
  }
}

/** Un campo vacío se muestra "—" (nunca "undefined"/"null"). */
function texto(valor: unknown): string {
  const s = valor == null ? '' : String(valor).trim();
  return s || '—';
}

/** Empresa: nombre comercial o, si no tiene, razón social. Persona: nombre y apellidos. */
function nombrePagador(p: any): string {
  if (p?.tipoUsuarioId === 1) return texto(p.nombreComercial || p.razonSocial);
  return texto([p?.nombre, p?.apellidos].filter(Boolean).join(' '));
}

/** Empresa: RUT de la empresa. Persona: su documento. */
function documentoPagador(p: any): string {
  return texto(p?.tipoUsuarioId === 1 ? p.rutEmpresa : p?.numeroId);
}
